// Client-side CSV parsing for the Unit Health imports. my.Scouting /
// Scoutbook reports carry a few key:value preamble lines, a "disclaimer"
// quote, then the real header + rows — so callers locate the header by the
// columns they expect rather than assuming line 1.

/** Minimal RFC4180 parser: quoted fields, embedded commas, and doubled ""
 *  as an escaped quote. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\r') {
      // skip; \n ends the line
    } else if (c === '\n') {
      row.push(field);
      field = '';
      rows.push(row);
      row = [];
    } else {
      field += c;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.length > 1 || r[0] !== '');
}

/** Rows keyed by header column name. `signatureColumns` must all be present
 *  (after stripping a leading '.' the reports sometimes prefix) for a row to
 *  count as the header. Throws if no such row is found. */
export function parseCsvRecords(
  text: string,
  signatureColumns: string[],
): Record<string, string>[] {
  const rows = parseCsv(text);
  const wanted = signatureColumns.map((c) => c.toLowerCase());
  let headerIdx = -1;
  let header: string[] = [];
  for (let i = 0; i < rows.length; i++) {
    const cells = rows[i].map((c) => c.replace(/^\.+/, '').trim());
    const lower = cells.map((c) => c.toLowerCase());
    if (wanted.every((w) => lower.includes(w))) {
      headerIdx = i;
      header = cells;
      break;
    }
  }
  if (headerIdx === -1) {
    throw new Error(`This file doesn't look right — expected columns: ${signatureColumns.join(', ')}`);
  }
  const out: Record<string, string>[] = [];
  for (let i = headerIdx + 1; i < rows.length; i++) {
    const cells = rows[i];
    if (cells.every((c) => c.trim() === '')) continue;
    const rec: Record<string, string> = {};
    header.forEach((h, j) => {
      // keys are lowercased so callers can use one casing regardless of how
      // a given report capitalizes its header (some are all-lower, the
      // Trained Leaders one is Capitalized_Snake_Case).
      rec[h.toLowerCase()] = (cells[j] ?? '').trim();
    });
    out.push(rec);
  }
  return out;
}

/** Pull "Date Report Generated: 09/05/2026" (or a YTD window's start year)
 *  from the preamble. Returns the raw text of the first matching line. */
export function findPreambleValue(text: string, label: string): string | null {
  for (const line of text.split(/\r?\n/).slice(0, 20)) {
    const idx = line.toLowerCase().indexOf(label.toLowerCase());
    if (idx >= 0) {
      return line
        .slice(idx + label.length)
        .replace(/^[:\s]+/, '')
        .trim();
    }
  }
  return null;
}
